import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FeatherIcon from '../../../components/common/FeatherIcon';
import { TIME_WINDOWS, matchSlashCommands } from './agentModel';
import ToolsControl from './ToolsControl';

const Menu = ({ children, className = 'up', label, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);
  return <div ref={ref} className={`sa3-menu ${className}`} role="menu" aria-label={label}>{children}</div>;
};

/**
 * The input box: multi-line text, slash commands, the time window, voice
 * input and Send/Stop. Keyboard: Enter sends, Shift+Enter breaks a line,
 * ↑ on an empty box recalls the last question, Esc stops a running answer.
 */
export default function Composer({
  value, onChange, onSend, onStop, onCommand, onEditLast,
  busy, textareaRef, placeholder, windowId, onWindow, voice, hideWindow = false, toolPrefs, onToolPrefs,
}) {
  const { t } = useTranslation('aiExplorer');
  const [menu, setMenu] = useState(null);        // null | 'window' | 'commands'
  const [cmdIndex, setCmdIndex] = useState(0);
  const commands = matchSlashCommands(value);
  const cmdOpen = commands.length > 0 && !busy;

  useEffect(() => { setCmdIndex(0); }, [value]);

  // Grow with the text up to 200px; an empty box takes its natural one-line height.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return undefined;
    const resize = () => {
      el.style.height = '';
      if (el.value) el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [value, textareaRef]);

  const runCommand = (c) => { onChange(''); onCommand(c); };

  const onKeyDown = (e) => {
    if (cmdOpen) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setCmdIndex((i) => (i + 1) % commands.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setCmdIndex((i) => (i - 1 + commands.length) % commands.length); return; }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey && !commands[cmdIndex].arg)) {
        e.preventDefault(); runCommand(commands[cmdIndex]); return;
      }
      if (e.key === 'Tab') { e.preventDefault(); onChange(`${commands[cmdIndex].usage.split(' ')[0]} `); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!busy) onSend(); return; }
    if (e.key === 'ArrowUp' && !value) { e.preventDefault(); onEditLast(); return; }
    if (e.key === 'Escape') { if (busy) onStop(); else setMenu(null); }
  };

  const windowLabel = t(`agent.window_${windowId}`);
  const canSend = value.trim().length > 0 && !busy;

  return (
    <div className="sa3-comp">
      {cmdOpen && (
        <div className="sa3-menu up" role="listbox" aria-label={t('agent.commands')}>
          <div className="sa3-mh">{t('agent.commands')}</div>
          {commands.map((c, i) => (
            <button key={c.id} type="button" role="option" aria-selected={i === cmdIndex} className={`sa3-mi${i === cmdIndex ? ' hl' : ''}`}
              onMouseEnter={() => setCmdIndex(i)} onClick={() => (c.arg ? onChange(`${c.usage.split(' ')[0]} `) : runCommand(c))}>
              <span className="sa3-cmd-usage">{c.usage}</span>
              <span>{t(`agent.cmd_${c.id}`)}</span>
            </button>
          ))}
        </div>
      )}

      {voice.listening ? (
        <div className="sa3-rec" aria-live="polite">
          <span className="sa3-dot" aria-hidden="true" />
          <span className="sa3-rec-t">{`${Math.floor(voice.elapsed / 60)}:${String(voice.elapsed % 60).padStart(2, '0')}`}</span>
          <span className="sa3-rec-l">{t('agent.listening')}</span>
          <button type="button" className="sa3-ibtn" onClick={voice.cancel} aria-label={t('agent.cancelRecording')} title={t('agent.cancelRecording')}><FeatherIcon name="x" size={16} /></button>
          <button type="button" className="sa3-send" onClick={voice.toggle} aria-label={t('agent.finishRecording')} title={t('agent.finishRecording')}><FeatherIcon name="check" size={16} /></button>
        </div>
      ) : (
        <>
          <textarea
            ref={textareaRef}
            id="ai-explorer-question-input"
            rows={1}
            value={voice.transcribing ? t('agent.transcribing') : value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            aria-label={t('agent.messageAgent')}
            disabled={voice.transcribing}
          />
          <div className="sa3-tools">
            <div style={{ position: 'relative' }}>
              <button type="button" className="sa3-ibtn" onClick={() => onChange(value.startsWith('/') ? value : '/')} aria-label={t('agent.commands')} title={t('agent.commands')} style={{ fontWeight: 700, fontSize: 16 }}>/</button>
            </div>
            {!hideWindow && <div style={{ position: 'relative' }}>
              <button type="button" className="sa3-pill" onClick={() => setMenu(menu === 'window' ? null : 'window')} aria-haspopup="menu" aria-expanded={menu === 'window'} title={t('agent.timeWindow')}>
                <FeatherIcon name="clock" size={14} />{windowLabel}<FeatherIcon name="chevron-down" size={14} />
              </button>
              {menu === 'window' && (
                <Menu label={t('agent.timeWindow')} onClose={() => setMenu(null)}>
                  <div className="sa3-mh">{t('agent.timeWindow')}</div>
                  {TIME_WINDOWS.map((w) => (
                    <button key={w.id} type="button" role="menuitemradio" aria-checked={w.id === windowId} className={`sa3-mi${w.id === windowId ? ' hl' : ''}`} onClick={() => { onWindow(w.id); setMenu(null); }}>
                      {t(`agent.window_${w.id}`)}
                      {w.id === windowId && <small><FeatherIcon name="check" size={14} /></small>}
                    </button>
                  ))}
                </Menu>
              )}
            </div>}
            {toolPrefs && !hideWindow && <ToolsControl prefs={toolPrefs} onChange={onToolPrefs} busy={busy} />}
            <span className="sa3-sp" />
            <div style={{ position: 'relative' }}>
              <button type="button" className="sa3-ibtn" onClick={voice.toggle} disabled={busy || voice.transcribing} aria-label={t('agent.voiceInput')} title={t('agent.voiceInput')}><FeatherIcon name="mic" size={16} /></button>
              {voice.pickerOpen && (
                <Menu className="up right" label={t('agent.selectMicrophone')} onClose={voice.closePicker}>
                  <div className="sa3-mh">{t('agent.selectMicrophone')}</div>
                  {voice.mics.map((mic) => (
                    <button key={mic.deviceId} type="button" role="menuitem" className="sa3-mi" onClick={() => voice.chooseMic(mic.deviceId)}>
                      <FeatherIcon name="mic" size={14} />{mic.label || mic.deviceId.slice(0, 8)}
                    </button>
                  ))}
                </Menu>
              )}
            </div>
            {busy ? (
              <button type="button" className="sa3-send stop" onClick={onStop} aria-label={t('agent.stop')} title={t('agent.stop')}><FeatherIcon name="square" size={14} /></button>
            ) : (
              <button type="button" id="ai-explorer-send-btn" className="sa3-send" onClick={onSend} disabled={!canSend} aria-label={t('agent.send')} title={t('agent.send')}><FeatherIcon name="arrow-up" size={18} /></button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
